import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-14 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">文章未找到</h1>
          <Link to="/" className="text-primary-500 mt-4 inline-block cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '同人创作指南',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://doujin-guide.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <article className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-10">
            {/* Breadcrumb */}
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: article.category, href: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />
            </div>

            {/* Article Header */}
            <header className="mb-8">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 mb-4 cursor-pointer hover:bg-primary-200 transition-colors"
              >
                {article.category}
              </Link>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line" />
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" />
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line" />
                  {article.readTime} 分钟阅读
                </span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Article Content */}
            <div
              className="prose-custom"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="flex flex-wrap items-center gap-2 mt-10 pt-8 border-t border-background-200/70">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-block px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-900"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Share */}
            <div className="flex items-center gap-3 mt-6">
              <span className="text-sm text-foreground-500">分享：</span>
              <a
                href="#"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer"
                aria-label="分享到微博"
              >
                <i className="ri-weibo-line text-sm" />
              </a>
              <a
                href="#"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer"
                aria-label="分享到微信"
              >
                <i className="ri-wechat-line text-sm" />
              </a>
              <a
                href="#"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer"
                aria-label="复制链接"
              >
                <i className="ri-link text-sm" />
              </a>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="bg-background-100 py-14 md:py-16">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((ra) => (
                    <ArticleCard
                      key={ra.id}
                      title={ra.title}
                      excerpt={ra.excerpt}
                      image={ra.image}
                      category={ra.category}
                      categorySlug={ra.categorySlug}
                      date={ra.date}
                      readTime={ra.readTime}
                      slug={ra.slug}
                    />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}